import React, { useState, useEffect } from 'react';
import { 
  getCategories, createCategory, updateCategory, deleteCategory, toggleCategoryStatus 
} from '../services/api';
import toast from 'react-hot-toast';

const AdminCategoryManager = () => {
  const [categories, setCategories] = useState([]);
  const [groupedCategories, setGroupedCategories] = useState({});
  const [activeType, setActiveType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const categoryTypes = [
    { value: 'all', label: 'All Categories', icon: 'fas fa-folder-open' },
    { value: 'product', label: 'Products', icon: 'fas fa-box' },
    { value: 'service', label: 'Services', icon: 'fas fa-cogs' },
    { value: 'training', label: 'Trainings', icon: 'fas fa-graduation-cap' },
    { value: 'partner', label: 'Partners', icon: 'fas fa-handshake' },
    { value: 'team', label: 'Team', icon: 'fas fa-users' },
    { value: 'testimonial', label: 'Testimonials', icon: 'fas fa-comment' },
    { value: 'event', label: 'Events', icon: 'fas fa-calendar' },
    { value: 'whyus', label: 'Why Us', icon: 'fas fa-star' },
    { value: 'career', label: 'Careers', icon: 'fas fa-briefcase' }
  ];

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await getCategories();
      // Handle different response structures
      let categoriesData = [];
      let groupedData = {};
      
      if (response && response.data && Array.isArray(response.data)) {
        categoriesData = response.data;
        groupedData = response.grouped || {};
      } else if (response && Array.isArray(response)) {
        categoriesData = response;
      } else if (response && response.categories && Array.isArray(response.categories)) {
        categoriesData = response.categories;
      }
      
      setCategories(categoriesData);
      setGroupedCategories(groupedData);
      
      console.log('Categories loaded:', categoriesData.length);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Error fetching categories');
      setCategories([]);
      setGroupedCategories({});
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (categoryData) => {
    try {
      if (editingItem) {
        await updateCategory(editingItem._id, categoryData);
        toast.success('Category updated successfully');
      } else {
        await createCategory(categoryData);
        toast.success('Category created successfully');
      }
      fetchCategories();
      setShowModal(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error(error.message || 'Error saving category');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await deleteCategory(id);
        toast.success('Category deleted successfully');
        fetchCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
        toast.error('Error deleting category');
      }
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      await toggleCategoryStatus(id);
      toast.success(`Category ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchCategories();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Error toggling status');
    }
  };

  const getDisplayCategories = () => {
    if (activeType === 'all') {
      return categories;
    }
    return categories.filter(cat => cat.type === activeType);
  };

  const getTypeIcon = (type) => {
    const typeMap = {
      product: '📦',
      service: '⚙️',
      training: '🎓',
      partner: '🤝',
      team: '👥',
      testimonial: '💬',
      event: '📅',
      whyus: '⭐',
      career: '💼'
    };
    return typeMap[type] || '🏷️';
  };

  const getTypeLabel = (type) => {
    const typeMap = {
      product: 'Products',
      service: 'Services',
      training: 'Trainings',
      partner: 'Partners',
      team: 'Team',
      testimonial: 'Testimonials',
      event: 'Events',
      whyus: 'Why Us',
      career: 'Careers'
    };
    return typeMap[type] || type;
  };

  const displayedCategories = getDisplayCategories();

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div className="spinner" style={{
          width: '40px',
          height: '40px',
          border: '4px solid #E2E8F0',
          borderTopColor: '#2C7A7B',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 1rem'
        }} />
        <p>Loading categories...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: '#0A192F', marginBottom: '0.5rem' }}>Category Manager</h2>
        <p style={{ color: '#5A6E7F' }}>Manage categories for Products, Services, Trainings, Partners, Team, Testimonials, Events, Why Us, and Careers</p>
        <p style={{ color: '#2C7A7B', fontSize: '0.875rem', marginTop: '0.5rem' }}>
          <i className="fas fa-database"></i> Total Categories: {categories.length}
        </p>
      </div>

      {/* Type Tabs */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        borderBottom: '2px solid #E2E8F0',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        paddingBottom: '0'
      }}>
        {categoryTypes.map((type) => (
          <button
            key={type.value}
            onClick={() => setActiveType(type.value)}
            style={{
              padding: '0.6rem 1.2rem',
              background: activeType === type.value ? '#2C7A7B' : 'transparent',
              color: activeType === type.value ? 'white' : '#5A6E7F',
              border: 'none',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s'
            }}
          >
            <i className={type.icon}></i>
            {type.label}
            {type.value !== 'all' && groupedCategories[type.value] && (
              <span style={{
                background: activeType === type.value ? 'rgba(255,255,255,0.2)' : '#E2E8F0',
                padding: '0.1rem 0.4rem',
                borderRadius: '20px',
                fontSize: '0.7rem',
                marginLeft: '0.3rem'
              }}>
                {groupedCategories[type.value]?.length || 0}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search and Add Button */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: '300px' }}>
          <i className="fas fa-search" style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#94A3B8'
          }} />
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem 0.5rem 0.5rem 2rem',
              border: '1px solid #E2E8F0',
              borderRadius: '8px',
              fontSize: '0.875rem'
            }}
          />
        </div>
        <button
          onClick={() => {
            setEditingItem(null);
            setShowModal(true);
          }}
          style={{
            padding: '0.5rem 1rem',
            background: '#2C7A7B',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <i className="fas fa-plus"></i>
          Add Category
        </button>
      </div>

      {/* Categories Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <thead style={{ background: '#F7F9FC' }}>
            <tr>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Type</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Icon</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Slug</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Order</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Status</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayedCategories
              .filter(cat => cat.name?.toLowerCase().includes(searchTerm.toLowerCase()))
              .map((category) => (
                <tr key={category._id} style={{ borderBottom: '1px solid #E2E8F0' }}>
                  <td style={{ padding: '1rem' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.3rem',
                      padding: '0.2rem 0.6rem',
                      background: '#E3F2FD',
                      borderRadius: '20px',
                      fontSize: '0.7rem'
                    }}>
                      {getTypeIcon(category.type)} {getTypeLabel(category.type)}
                    </span>
                  </td>
                  <td style={{ padding: '1rem', fontSize: '1.5rem' }}>{category.icon || getTypeIcon(category.type)}</td>
                  <td style={{ padding: '1rem', fontWeight: 500 }}>{category.name}</td>
                  <td style={{ padding: '1rem', fontFamily: 'monospace', fontSize: '0.8rem' }}>{category.slug}</td>
                  <td style={{ padding: '1rem' }}>
                    <input
                      type="number"
                      value={category.order}
                      onChange={async (e) => {
                        await updateCategory(category._id, { ...category, order: parseInt(e.target.value) });
                        fetchCategories();
                      }}
                      style={{ width: '60px', padding: '0.25rem', border: '1px solid #E2E8F0', borderRadius: '4px' }}
                    />
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <button
                      onClick={() => handleToggle(category._id, category.isActive)}
                      style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '20px',
                        border: 'none',
                        background: category.isActive ? '#D4EDDA' : '#F8D7DA',
                        color: category.isActive ? '#155724' : '#721C24',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                    >
                      {category.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <button
                      onClick={() => {
                        setEditingItem(category);
                        setShowModal(true);
                      }}
                      style={{
                        padding: '0.25rem 0.75rem',
                        background: '#2C7A7B',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginRight: '0.5rem'
                      }}
                    >
                      <i className="fas fa-edit"></i> Edit
                    </button>
                    <button
                      onClick={() => handleDelete(category._id)}
                      style={{
                        padding: '0.25rem 0.75rem',
                        background: '#DC2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      <i className="fas fa-trash"></i> Delete
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        
        {displayedCategories.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem', background: 'white', borderRadius: '12px', marginTop: '1rem' }}>
            <i className="fas fa-folder-open" style={{ fontSize: '3rem', color: '#CBD5E1' }} />
            <p style={{ marginTop: '1rem', color: '#5A6E7F' }}>
              {searchTerm ? 'No categories match your search' : 'No categories found'}
            </p>
            {!searchTerm && (
              <button
                onClick={() => {
                  setEditingItem(null);
                  setShowModal(true);
                }}
                style={{
                  marginTop: '1rem',
                  padding: '0.5rem 1rem',
                  background: '#2C7A7B',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                <i className="fas fa-plus"></i> Create First Category
              </button>
            )}
          </div>
        )}
      </div>

      {/* Category Modal */}
      {showModal && (
        <CategoryModal
          editingItem={editingItem}
          categoryTypes={categoryTypes}
          onSubmit={handleSubmit}
          onCancel={() => {
            setShowModal(false);
            setEditingItem(null);
          }}
        />
      )}
    </div>
  );
};

// Category Modal Component
const CategoryModal = ({ editingItem, categoryTypes, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: editingItem?.name || '',
    type: editingItem?.type || 'product',
    description: editingItem?.description || '',
    icon: editingItem?.icon || '',
    order: editingItem?.order || 0,
    isActive: editingItem?.isActive !== undefined ? editingItem.isActive : true
  });

  const typeOptions = categoryTypes.filter(t => t.value !== 'all');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }} onClick={onCancel}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '2rem',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ color: '#0A192F', margin: 0 }}>
            {editingItem ? 'Edit Category' : 'Add New Category'}
          </h3>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Category Type *</label>
            <select
              required
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}
            >
              {typeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Category Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter category name"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              placeholder="Category description"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Icon (Emoji)</label>
            <input
              type="text"
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              placeholder="📦"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Display Order</label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}
            />
            <small style={{ color: '#5A6E7F' }}>Lower numbers appear first</small>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              <span>Active</span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
            <button type="submit" style={{ flex: 1, padding: '0.75rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
              <i className="fas fa-save"></i> Save Category
            </button>
            <button type="button" onClick={onCancel} style={{ flex: 1, padding: '0.75rem', background: '#5A6E7F', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminCategoryManager;